import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { PARTICLE_COUNTS } from '@/domain/celebration';
import { usePrefersReducedMotion } from '@/lib/hooks';
import styles from './Celebration.module.css';

/**
 * Tiny hearts and sparks, for the moments that earned them.
 *
 * Deliberately not a confetti library: no canvas, no physics loop, no
 * dependency. A handful of spans with a CSS keyframe each, removed from the
 * DOM the moment they finish. On a mid-range phone that is a few compositor
 * layers for about a second, which is the difference between "delightful" and
 * "why is this questionnaire janky".
 *
 * Under `prefers-reduced-motion` the hook simply refuses to emit — callers get
 * `false` back and apply their static highlight instead. Nothing is animated
 * at 1ms, and nothing floats across the screen.
 */

export type BurstKind = 'sparkle' | 'finale';

/** Glyph, colour token and relative size. Weighted so hearts stay rare. */
const GLYPHS: Array<{ glyph: string; colour: string; size: number }> = [
  { glyph: '✦', colour: 'var(--align-strong)', size: 1 },
  { glyph: '✧', colour: 'var(--voice-james)', size: 0.85 },
  { glyph: '✦', colour: 'var(--voice-you)', size: 0.7 },
  { glyph: '·', colour: 'var(--align-strong)', size: 1.6 },
  { glyph: '♥', colour: 'var(--c-raspberry-500)', size: 1.15 },
];

/**
 * How big a particle is, in rem, at `size: 1`. The sizes above are ratios to
 * each other; this is the one number that decides how loud the whole thing
 * feels. Turn it up and the burst has to travel further too, or ten large
 * glyphs land on top of one another and read as a smudge — hence the matching
 * spread and rise below.
 */
const BASE_SIZE_REM = 2.1;

interface Particle {
  id: number;
  glyph: string;
  style: CSSProperties;
}

interface Burst {
  id: number;
  kind: BurstKind;
  x: number;
  y: number;
  particles: Particle[];
  /** Longest particle life, used to schedule removal. */
  lifetime: number;
}

let nextId = 0;

function buildParticles(kind: BurstKind): { particles: Particle[]; lifetime: number } {
  const count = kind === 'finale' ? PARTICLE_COUNTS.finale : PARTICLE_COUNTS.sparkle;
  const spread = kind === 'finale' ? 340 : 230;
  const rise = kind === 'finale' ? 260 : 200;

  let lifetime = 0;
  const particles: Particle[] = [];

  for (let index = 0; index < count; index += 1) {
    const glyph = GLYPHS[index % GLYPHS.length];

    // Fan out from the anchor, biased upward. Random enough to look organic,
    // bounded enough that nothing ever lands on top of the controls.
    const dx = (Math.random() - 0.5) * spread;
    const dy = -(rise * (0.55 + Math.random() * 0.65));
    const duration = 1000 + Math.random() * 600; // 1000-1600ms
    const delay = index * (kind === 'finale' ? 28 : 34);

    lifetime = Math.max(lifetime, duration + delay);

    particles.push({
      id: index,
      glyph: glyph.glyph,
      style: {
        '--particle-dx': `${dx.toFixed(1)}px`,
        '--particle-dy': `${dy.toFixed(1)}px`,
        '--particle-rotate': `${(Math.random() * 120 - 60).toFixed(0)}deg`,
        '--particle-duration': `${duration.toFixed(0)}ms`,
        '--particle-delay': `${delay}ms`,
        '--particle-colour': glyph.colour,
        '--particle-size': `${(
          glyph.size *
          BASE_SIZE_REM *
          (kind === 'finale' ? 1.25 : 1)
        ).toFixed(2)}rem`,
      } as CSSProperties,
    });
  }

  return { particles, lifetime };
}

interface CelebrationApi {
  /**
   * Emits a burst centred on the top of `anchor`.
   *
   * Returns `true` if anything was emitted — `false` under reduced motion, or
   * when there is nothing to anchor to, so the caller can fall back to a
   * static treatment.
   */
  celebrate: (anchor: HTMLElement | null, kind?: BurstKind) => boolean;
  /** Render this somewhere in the tree. It portals itself to <body>. */
  layer: ReactNode;
}

export function useCelebration(): CelebrationApi {
  const reducedMotion = usePrefersReducedMotion();
  const [bursts, setBursts] = useState<Burst[]>([]);
  const timers = useRef<number[]>([]);

  useEffect(
    () => () => {
      for (const timer of timers.current) window.clearTimeout(timer);
      timers.current = [];
    },
    [],
  );

  // A change of heart mid-flight clears the sky immediately.
  useEffect(() => {
    if (reducedMotion) setBursts([]);
  }, [reducedMotion]);

  const celebrate = useCallback<CelebrationApi['celebrate']>(
    (anchor, kind = 'sparkle') => {
      if (reducedMotion || !anchor || typeof window === 'undefined') return false;

      const rect = anchor.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;

      const { particles, lifetime } = buildParticles(kind);
      const id = (nextId += 1);

      setBursts((current) => [
        ...current,
        {
          id,
          kind,
          x: rect.left + rect.width / 2,
          y: rect.top + Math.min(rect.height / 2, 90),
          particles,
          lifetime,
        },
      ]);

      const timer = window.setTimeout(() => {
        setBursts((current) => current.filter((burst) => burst.id !== id));
        timers.current = timers.current.filter((t) => t !== timer);
      }, lifetime + 80);

      timers.current.push(timer);
      return true;
    },
    [reducedMotion],
  );

  const layer =
    bursts.length === 0 || typeof document === 'undefined'
      ? null
      : createPortal(
          <div className={styles.layer} aria-hidden="true" data-celebration-layer="">
            {bursts.map((burst) => (
              <div
                key={burst.id}
                className={styles.burst}
                data-celebration={burst.kind}
                style={
                  {
                    '--burst-x': `${burst.x}px`,
                    '--burst-y': `${burst.y}px`,
                  } as CSSProperties
                }
              >
                {burst.particles.map((particle) => (
                  <span key={particle.id} className={styles.particle} style={particle.style}>
                    {particle.glyph}
                  </span>
                ))}
              </div>
            ))}
          </div>,
          document.body,
        );

  return { celebrate, layer };
}
